import React, { createContext, useContext, useState, useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { authAPI } from "@/lib/api";
import { LanguageProvider } from "@/lib/i18n";
import { registerServiceWorker, subscribeToPush, requestNotificationPermission, isPushSupported } from "@/lib/push";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import HomePage from "@/pages/HomePage";
import ActivitiesPage from "@/pages/ActivitiesPage";
import BookingPage from "@/pages/BookingPage";
import AuthPage from "@/pages/AuthPage";
import DashboardPage from "@/pages/DashboardPage";
import GiftCardsPage from "@/pages/GiftCardsPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import AdminPage from "@/pages/AdminPage";
import LegalPage from "@/pages/LegalPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import CreditsPage from "@/pages/CreditsPage";
import ProfilePage from "@/pages/ProfilePage";
import ContractPage from "@/pages/ContractPage";
import ParticipantsPage from "@/pages/ParticipantsPage";
import PhotoGalleryPage from "@/pages/PhotoGalleryPage";
import { CookiePopup } from "@/components/CookiePopup";
import { InstallPrompt } from "@/components/InstallPrompt";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('rw_token');
    const savedUser = localStorage.getItem('rw_user');
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch { /* ignore */ }
    }
    setLoading(false);
    // Register service worker for PWA + push
    registerServiceWorker();
  }, []);

  const login = async (token, userData) => {
    localStorage.setItem('rw_token', token);
    localStorage.setItem('rw_user', JSON.stringify(userData));
    setUser(userData);
    // Auto-subscribe to push notifications after login
    if (isPushSupported()) {
      const perm = await requestNotificationPermission();
      if (perm === 'granted') {
        await subscribeToPush(token, userData.site_id || 'site-hyeres');
      }
    }
  };

  const logout = () => {
    localStorage.removeItem('rw_token');
    localStorage.removeItem('rw_user');
    setUser(null);
  };

  const updateUser = (userData) => {
    localStorage.setItem('rw_user', JSON.stringify(userData));
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user) return <Navigate to="/auth" state={{ from: location.pathname + location.search }} replace />;
  if (adminOnly && !['super_admin', 'franchise_admin'].includes(user.role)) return <Navigate to="/" />;
  return children;
};

function useIsIframe() {
  try { return window.self !== window.top; } catch { return true; }
}

function App() {
  const isIframe = useIsIframe();

  return (
    <LanguageProvider>
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <div className="min-h-screen flex flex-col">
          {!isIframe && <Navbar />}
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/activities/:siteId" element={<ActivitiesPage />} />
              <Route path="/booking/:activityId" element={<BookingPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/dashboard" element={
                <ProtectedRoute><DashboardPage /></ProtectedRoute>
              } />
              <Route path="/gift-cards" element={<GiftCardsPage />} />
              <Route path="/credits" element={
                <ProtectedRoute><CreditsPage /></ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute><ProfilePage /></ProtectedRoute>
              } />
              <Route path="/contract/:bookingId" element={
                <ProtectedRoute><ContractPage /></ProtectedRoute>
              } />
              <Route path="/booking/:bookingId/participants" element={
                <ParticipantsPage />
              } />
              <Route path="/booking/:bookingId/photos" element={
                <ProtectedRoute><PhotoGalleryPage /></ProtectedRoute>
              } />
              <Route path="/payment/success" element={<PaymentSuccessPage />} />
              <Route path="/admin" element={
                <ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>
              } />
              <Route path="/cgv" element={<LegalPage />} />
              <Route path="/mentions-legales" element={<LegalPage />} />
              <Route path="/politique-cookies" element={<LegalPage />} />
            </Routes>
          </main>
          {!isIframe && <Footer />}
          {!isIframe && <CookiePopup />}
          {!isIframe && <InstallPrompt />}
        </div>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
